import { useState } from 'react';
import { Alert, Button, Checkbox } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { submitDeclaration } from '@/api/dataroom';
import { normalizeError } from '@/api/errors';
import type { DeclarationSpec, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';

/**
 * DECLARATION task (OL-22): the applicant reads the server-resolved text in full, ticks, and accepts.
 * Only the VERSION goes back — the server hashes the text it resolved, so what is on record is
 * exactly what this page showed. A 409 means the text moved on: we re-read the tasks and ask again.
 */
export function DeclarationTask({
  task,
  onStatus,
  onRefresh,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
  onRefresh?: () => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const spec = task.spec as DeclarationSpec | undefined;
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (task.status === 'ACCEPTED' || done) {
    return <Alert type="success" showIcon message={t('declaration.accepted')} />;
  }
  if (!task.declarationText || !spec?.version) {
    return <Alert type="warning" showIcon message={t('declaration.unavailable')} />;
  }

  async function accept() {
    if (!spec) return;
    setBusy(true);
    setError(null);
    try {
      const res = await submitDeclaration(task.taskId, spec.version);
      onStatus(res.status);
      setDone(true);
    } catch (err) {
      if (normalizeError(err).kind === 'CONFLICT') {
        setChecked(false);
        setError(t('declaration.changed'));
        onRefresh?.();
      } else {
        setError(t('error.inline'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dr-stack">
      <div className="dr-meta dr-legal">{text('readPrompt', 'declaration.readPrompt')}</div>
      <div className="dr-declaration dr-legal" role="document" tabIndex={0}>
        {task.declarationText}
      </div>
      <div className="dr-meta dr-legal">{t('declaration.versionLabel', { version: spec.version })}</div>
      {error && <Alert type="error" showIcon message={error} />}
      <Checkbox className="dr-legal" checked={checked} onChange={(e) => setChecked(e.target.checked)}>
        {t('declaration.checkbox')}
      </Checkbox>
      <Button
        type="primary"
        size="large"
        block
        disabled={!checked}
        loading={busy}
        icon={<CheckOutlined />}
        onClick={accept}
      >
        {busy ? t('declaration.accepting') : text('submit', 'declaration.accept')}
      </Button>
    </div>
  );
}
